import imageCompression from "browser-image-compression";
import { PDFDocument } from "pdf-lib";

export async function compressImage(file: File, targetSizeMB = 1): Promise<File> {
    const targetBytes = targetSizeMB * 1024 * 1024;
    if (file.size <= targetBytes) return file;

    try {
        const compressed = await imageCompression(file, {
            maxSizeMB: targetSizeMB,
            maxWidthOrHeight: 1920,
            useWebWorker: true,
        });
        return new File([compressed], file.name, { type: compressed.type || file.type });
    } catch (err) {
        console.error(`Image compression failed for "${file.name}":`, err);
        return file;
    }
}

/**
 * Compress a PDF using pdf-lib's object stream compression.
 * Returns null for non-PDF files over 1MB so the caller can show a toast.
 * Returns the original file if it's already under 1MB.
 */
export async function compressDocument(file: File, targetSizeMB = 1): Promise<File | null> {
    const targetBytes = targetSizeMB * 1024 * 1024;
    if (file.size <= targetBytes) return file;

    if (file.type !== "application/pdf") {
        return null;
    }

    try {
        const arrayBuffer = await file.arrayBuffer();
        const pdfDoc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true });
        const compressedBytes = await pdfDoc.save({ useObjectStreams: true });
        const blob = new Blob([compressedBytes.buffer.slice(0) as ArrayBuffer], { type: "application/pdf" });
        const compressedFile = new File([blob], file.name, { type: "application/pdf" });

        if (compressedFile.size > targetBytes) {
            console.warn(`"${file.name}" is still over ${targetSizeMB}MB after PDF compression.`);
        }

        return compressedFile;
    } catch (err) {
        console.error(`PDF compression failed for "${file.name}":`, err);
        return file;
    }
}